"use client";
import cn from "classnames";
import { type ComponentPropsWithoutRef, type Ref } from "react";
import Heading from "@/components/common/Heading";
import Paragraph from "@/components/common/Paragraph";
import { SiteBuilderModuleTextFieldType } from "@/constants/site-builder";
import { FontVariant } from "@/constants/theme";
import { useCoreTheme } from "@/context/coreTheme";
import { getTextAlignmentClassName } from "@/utils/style/getTextAlignmentClassName";
import { getTextColorClassName } from "@/utils/style/getTextColorClassName";
import type {
  SiteBuilderModuleHeaderField,
  SiteBuilderModuleTextField,
} from "@/types/site-builder";
import s from "./TextField.module.scss";
import { getDefaultFontVariant } from "./utils";

export type FieldType =
  | "pre-title"
  | "title"
  | "sub-title"
  | "intro"
  | "outro"
  | "text-x-small"
  | "text-small"
  | "text-regular"
  | "text-large"
  | "text-x-large"
  | "text-xx-large"
  | "bullet-points"
  | "pull-quote"
  | "header-h1"
  | "header-h2"
  | "header-h3"
  | "header-h4"
  | "header-h5"
  | "header-h6";

export interface TextFieldProps
  extends Pick<ComponentPropsWithoutRef<"p">, "className"> {
  type: FieldType;
  field: SiteBuilderModuleTextField | SiteBuilderModuleHeaderField;
  ref?: Ref<HTMLDivElement>;
}

export default function TextField(props: TextFieldProps) {
  const { field, type, ref } = props;
  const theme = useCoreTheme();

  if (!field.text) return null;

  const variant =
    field.fontVariant === FontVariant.Default
      ? getDefaultFontVariant(theme, type)
      : field.fontVariant;

  const className = cn(
    props.className,
    s.text,
    {
      [s["text-x-small"]]: variant === FontVariant.BodyXSmall,
      [s["text-small"]]: variant === FontVariant.BodySmall,
      [s["text-regular"]]: variant === FontVariant.BodyRegular,
      [s["text-large"]]: variant === FontVariant.BodyLarge,
      [s["text-x-large"]]: variant === FontVariant.BodyXLarge,
      [s["text-xx-large"]]: variant === FontVariant.BodyXXLarge,
      [s["bullet-points"]]: variant === FontVariant.BulletPoints,
      [s["pull-quote"]]: variant === FontVariant.PullQuote,
      [s["h-1"]]: variant === FontVariant.HeaderH1,
      [s["h-2"]]: variant === FontVariant.HeaderH2,
      [s["h-3"]]: variant === FontVariant.HeaderH3,
      [s["h-4"]]: variant === FontVariant.HeaderH4,
      [s["h-5"]]: variant === FontVariant.HeaderH5,
      [s["h-6"]]: variant === FontVariant.HeaderH6,
    },
    "informa-module-text-field",
    `informa-module-${type}`,
    field.color && getTextColorClassName(field.color.value),
    getTextAlignmentClassName(field.alignment),
  );

  if (field.type === SiteBuilderModuleTextFieldType.Header) {
    return (
      <Heading
        type="rich"
        text={field.text}
        tag={field.tag}
        className={className}
        ref={ref}
      />
    );
  }

  return <Paragraph text={field.text} ref={ref} className={className} />;
}
